import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { isLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { formatLaborLocationLabel } from "@/lib/labor/laborPageKey";
import { flashAndBumpToOverview } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborSession } from "@/lib/labor/useLaborSession";

import type { KittingCartSlot } from "./kittingCartModel";
import { KITTING_LABOR_FLASH_STORAGE_KEY } from "./kittingLaborFlash";
import { kittingPickLaborPageKeyForRun, kittingPickLaborPageKeyForSlots } from "./kittingLaborPage";
import { syncKittingPickLaborAllocations } from "./kittingPickLaborApi";
import { buildKittingPickLaborAllocations } from "./kittingPickLaborModel";
import type { KittingPickLine } from "./kittingPickModel";

export function useKittingPickLabor(input: {
  slotIds: readonly string[];
  runId?: number | null;
  selectedSlots: readonly KittingCartSlot[];
  scopedPickLines: readonly KittingPickLine[];
  canWrite: boolean;
}): {
  laborGateReady: boolean;
  touchActivity: () => void;
} {
  const navigate = useNavigate();
  const allocationSyncRef = useRef<string>("");

  const laborPage = useMemo(() => {
    if (input.runId != null) return kittingPickLaborPageKeyForRun(input.runId);
    if (input.slotIds.length > 0) return kittingPickLaborPageKeyForSlots(input.slotIds);
    return "";
  }, [input.runId, input.slotIds]);

  const bumpKittingLaborToQueueWithFlash = useCallback(
    (message: string) => {
      flashAndBumpToOverview({
        navigate,
        to: "/manufacturing/kitting",
        storageKey: KITTING_LABOR_FLASH_STORAGE_KEY,
        message,
        replace: true,
      });
    },
    [navigate],
  );

  const labor = useLaborSession({
    page: laborPage,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Picking",
    autoStart:
      laborPage !== "" && (input.canWrite || laborRemotePersistenceDisabled()),
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      bumpKittingLaborToQueueWithFlash(
        "Clocked out due to inactivity on this pick. Open the jobs again from Picking when you return.",
      ),
    onPersistedSessionLost: () =>
      bumpKittingLaborToQueueWithFlash(
        "Your hub labor session for this pick ended. Return to Picking and open the pick again to clock back in.",
      ),
    onSessionEndedRemotely: () =>
      bumpKittingLaborToQueueWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the pick again from Picking when you want to continue.",
      ),
    onStopped: () =>
      bumpKittingLaborToQueueWithFlash(
        "Clocked out. Open the pick again from Picking when you want to continue.",
      ),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const laborGateReady = laborPage !== "" && labor.gateReady;

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (input.slotIds.length === 0) return;
    if (labor.loading) return;
    if (isLaborOwnerDeparting()) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpKittingLaborToQueueWithFlash(
          `Finish clocking out of ${formatLaborLocationLabel(labor.activeOnOtherPage)} before opening this pick. Return to the other station, stop that session, then try again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [input.slotIds.length, labor.loading, labor.activeOnOtherPage, bumpKittingLaborToQueueWithFlash]);

  const laborGateFailureBumpRef = useRef(false);
  useEffect(() => {
    if (labor.gateReady) {
      laborGateFailureBumpRef.current = false;
    }
  }, [labor.gateReady]);

  useEffect(() => {
    if (input.slotIds.length === 0) return;
    if (labor.loading || labor.gateReady) return;
    if (labor.activeOnOtherPage) return;
    if (!labor.banner?.message) return;
    if (laborGateFailureBumpRef.current) return;
    laborGateFailureBumpRef.current = true;
    bumpKittingLaborToQueueWithFlash(labor.banner.message);
  }, [
    input.slotIds.length,
    labor.loading,
    labor.gateReady,
    labor.activeOnOtherPage,
    labor.banner?.message,
    bumpKittingLaborToQueueWithFlash,
  ]);

  const allocationPayload = useMemo(() => {
    if (!laborGateReady || input.selectedSlots.length === 0) return null;
    return buildKittingPickLaborAllocations({
      pickLines: input.scopedPickLines,
      slots: input.selectedSlots,
    }).map((row) => ({
      slotKey: row.slotKey,
      quantity: row.quantity,
      target: row.target,
    }));
  }, [input.scopedPickLines, input.selectedSlots, laborGateReady]);

  useEffect(() => {
    if (!laborGateReady || labor.sessionId == null || labor.sessionId <= 0 || allocationPayload == null) {
      return;
    }
    const signature = allocationPayload
      .map((row) => `${row.slotKey}:${String(row.quantity)}:${row.target.kind}`)
      .join("|");
    if (signature === allocationSyncRef.current) return;
    allocationSyncRef.current = signature;
    void syncKittingPickLaborAllocations({
      sessionId: labor.sessionId,
      allocations: allocationPayload,
    }).catch(() => {
      allocationSyncRef.current = "";
    });
  }, [allocationPayload, labor.sessionId, laborGateReady]);

  return {
    laborGateReady,
    touchActivity: labor.touchActivity,
  };
}
